<script lang="ts">
    import {ChevronLeft, ChevronRight} from '@lucide/svelte';
    import Step1Basisgegevens from './lib/components/steps/Step1Basisgegevens.svelte';
    import Step2Expertise from './lib/components/steps/Step2Expertise.svelte';
    import Step3OverbruggingMethodieken from './lib/components/steps/Step3OverbruggingMethodieken.svelte';
    import Step4Afronding from './lib/components/steps/Step4Afronding.svelte';
    import {
        canProceed,
        INITIAL_FORM_DATA,
        nextStep as getNextStep,
        prevStep as getPrevStep,
        TOTAL_STEPS,
        toggleList as toggleListState,
        updateField as updateFieldState,
    } from './lib/form/state';
    import type {FormData, Step, ToggleList, UpdateField} from './lib/form/types';
    import Button from "./lib/components/common/Button.svelte";
    import {onMount, tick} from "svelte";
    import {Session} from "./lib/form/session";

    let step = $state<Step>(1);
    let formData = $state<FormData>({...INITIAL_FORM_DATA});

    let container = $state<HTMLElement>();


    let {
        onsubmit,
    }: {
        onsubmit: (data: FormData) => void;
    } = $props();


    let step1Valid = $state(false);
    let step2Valid = $state(false);
    let step3Valid = $state(false);
    let step4Valid = $state(false);

    let canProceedVal = $derived.by(() => {
        switch (true) {
            case step === 1 && step1Valid:
            case step === 2 && step2Valid:
            case step === 3 && step3Valid:
            case step === 4 && step4Valid:
                return true;
        }
        return false;
    });

    const updateField: UpdateField = (field, value) => {
        formData = updateFieldState(formData, field, value);
    };

    const toggleList: ToggleList = (field, value, max = null) => {
        formData = toggleListState(formData, field, value, max);
    };

    /**
     * trigger validation errors by marking the input fields as dirty
     * triggering blur makes the inputs think they have been touched.
     */
    async function markFieldsAsDirty() {
        if (!container) return;

        // trigger events
        const inputs = container.querySelectorAll('input, select, textarea');
        const event = new Event('blur');
        inputs.forEach(input => input.dispatchEvent(event));

        // wait for rerender
        await tick();

        // scroll the first error into view if any
        const el = container.querySelector('.input-has-error');
        el && el.scrollIntoView({
            behavior: "smooth"
        })
    }

    const goToNextStep = (): void => {
        if (!canProceedVal) {
            markFieldsAsDirty();
            return;
        }

        if (step === 4) {
            onsubmit(formData)
            return;
        }

        step = getNextStep(step);
    };

    const goToPreviousStep = (): void => {
        step = getPrevStep(step);
    };


    // save the data
    $effect(() => {
        Session.store(formData, step)
    })

    onMount(() => {
        const loaded = Session.load();
        if (loaded) {
            formData = loaded.data
            step = loaded.step
        }

        window.vragenlijst_psycholoog_helpers = {
            setStep: (_step: Step) => {
                step = _step;
            }
        }
    })


</script>

<div
        class="text-slate-900 flex w-full max-w-2xl flex-col overflow-hidden rounded-[3rem] border border-slate-200/50 bg-white shadow-2xl"
>
    <div
            class="flex items-center justify-between border-b border-solid border-slate-100 bg-white/80 px-10 py-6 backdrop-blur-md"
    >
        <div class="flex gap-2">
            {#each Array.from({length: TOTAL_STEPS}, (_, index) => index + 1) as currentStep}
                <div
                        class={`h-1.5 rounded-full transition-all duration-500 ${step >= currentStep
              ? 'w-10 bg-[#7C46AF]'
              : 'w-4 bg-slate-200'}`}
                ></div>
            {/each}
        </div>
        <div class="text-right">
            <span class="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{formData.role}</span>
            <div class="mt-1.5 inline-block rounded-full bg-[#CDB6E2] px-3 py-1 text-[9px] font-bold text-[#7C46AF]">
                Stap {step} van {TOTAL_STEPS}
            </div>
        </div>
    </div>

    <div class="custom-scrollbar min-h-[620px] overflow-y-auto p-10"
         bind:this={container}
    >
        {#if step === 1}
            <Step1Basisgegevens bind:valid={step1Valid} {formData} {updateField} {toggleList}/>
        {:else if step === 2}
            <Step2Expertise
                    bind:valid={step2Valid}
                    {formData}
                    {updateField}
                    {toggleList}
            />
        {:else if step === 3}
            <Step3OverbruggingMethodieken bind:valid={step3Valid} {formData} {updateField} {toggleList}/>
        {:else}
            <Step4Afronding bind:valid={step4Valid} {formData} {updateField}/>
        {/if}
    </div>

    <div class="mt-auto flex items-center justify-between border-solid border-t border-slate-100 bg-slate-50/50 p-8 backdrop-blur-md">
        {#if step > 1}
            <Button
                    onclick={goToPreviousStep}
                    disabled={step === 1}
                    size="lg"
                    class="flex items-center"
            >
                <ChevronLeft size={20}/>
                Vorige
            </Button>
        {:else}
            <div></div>
        {/if}

        <Button
                onclick={goToNextStep}
                active={canProceedVal}
                size="lg"
                class="flex items-center"
        >
            {step === 4 ? 'Opslaan' : 'Volgende'}
            <ChevronRight size={20}/>
        </Button>

    </div>
</div>

<style>
    @import "tailwindcss";
</style>
